import * as Sentry from '@sentry/vue';
import { postData } from '@/api';

const state = {
    isInternalRoute: false,
    user: {
        name: '',
        email: '',
        role: { abilities: [], id: null },
        two_factor_confirmed_at: null,
        two_factor_provider: null,
        user_map_opt_in: false
    },
    isAuthenticated: false,
    advertiser_id: null,
    redirectPath: null,
    isOnVpn: false,
    requires2FA: false,
    pendingRoute: null,
    preferences: { favorites: [] } // Store the favorites array
};

const getters = {
    isOnVpn: (state) => state.isOnVpn,
    isInternalUser: (state, getters) => {
        return getters.userCan('isInternalUser');
    },
    isInternalRoute: (state) => state.isInternalRoute,
    userCan: (state) => (abilityName) => {
        if (!state.user || !state.user.role) {
            return false;
        }

        if (state.user.role && state.user.role.abilities.some((ability) => ability.name === '*')) {
            return true;
        }

        const roleCan = state?.user?.role && state?.user?.role?.abilities.some((ability) => ability.name === abilityName);
        let overrideCan = false;
        let overrides = [];

        if (state?.user?.meta) {
            overrides = state?.user?.meta.filter((meta) => meta.key === 'ability_overrides');
        }
        if (overrides.length > 0) {
            const abilityOverrides = overrides[0].value;
            overrideCan = JSON.parse(abilityOverrides).some((ability) => ability === abilityName);
        }
        return roleCan || overrideCan;
    },
    getUser: (state) => state.user,
    userName: (state) => state.user.name,
    userFirstName: (state) => state.user.name.split(' ')[0],
    isAuthenticated: (state) => state.isAuthenticated,
    getHasTwoFactor: (state) => state.user.two_factor_confirmed_at !== null,
    id: (state) => state.user.id,
    redirectPath: (state) => state.redirectPath,
    requires2FA: (state) => state.requires2FA,
    pendingRoute: (state) => state.pendingRoute,
    favorites: (state) => state.preferences.favorites
};

const actions = {
    setIsInternalRoute({ commit }, isInternalRoute) {
        commit('setIsInternalRoute', isInternalRoute);
    },
    setIsOnVPN({ commit }, isOnVpn) {
        commit('setIsOnVpn', isOnVpn);
    },
    async logout({ commit }) {
        commit('setUser', { name: '', email: '', role: null, two_factor_confirmed_at: null, advertiser_id: null });
        commit('setAuthenticated', false);
        commit('clearFavorites'); // Clear favorites from store (no localStorage action here)
    },
    // Action to add a favorite item
    addFavorite({ commit }, favorite) {
        commit('addFavorite', favorite); // Commit the mutation to add favorite to the store
    },
    // Action to remove a favorite item
    removeFavorite({ commit }, url) {
        commit('removeFavorite', url); // Commit the mutation to remove favorite from the store
    },
    // Action to load favorites (no longer loads from localStorage)
    loadFavorites({ commit }) {
        console.log('No longer loading favorites from localStorage');
        commit('setFavorites', favoritesFromStorage);
    },
    setFavorites({ commit }, favorites) {
        commit('setFavorites', favorites);
    }
};

const mutations = {
    setIsInternalRoute(state, isInternalRoute) {
        state.isInternalRoute = isInternalRoute;
    },
    setIsOnVpn(state, isOnVpn) {
        state.isOnVpn = isOnVpn;
    },
    setUser(state, user) {
        state.user = user;
        // Set user in Sentry for monitoring
        if (user && user.id && user.email) {
            Sentry.setUser({ id: user.id, email: user.email });
        }
    },
    setAuthenticated(state, isAuthenticated) {
        state.isAuthenticated = isAuthenticated;
    },
    setRedirectPath(state, path) {
        state.redirectPath = path;
    },
    setRequires2FA(state, requires2FA) {
        state.requires2FA = requires2FA;
        return true;
    },
    savePendingRoute(state, route) {
        state.pendingRoute = route;
    },
    clearPendingRoute(state) {
        state.pendingRoute = null;
    },
    // Mutations to modify favorites
    setFavorites(state, favorites) {
        console.log('setFavorites action triggered', favorites);
        state.preferences.favorites = favorites;
    },

    addFavorite(state, favorite) {
        console.log('addFavorite mutation', favorite);
        if (!state.preferences.favorites.some((fav) => fav.url === favorite.url)) {
            state.preferences.favorites = [...state.preferences.favorites, favorite];
        }
    },
    removeFavorite(state, url) {
        state.preferences.favorites = state.preferences.favorites.filter((fav) => fav.url !== url);
    },
    clearFavorites(state) {
        state.preferences.favorites = []; // Clear favorites from store
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
